import React, { useEffect, useState, useRef } from 'react';
import styled from 'styled-components';

const StyledWrapper = styled.div`
  .container {
    width: 1400px;
    height: 900px;
    margin: 0 auto;
    background: rgba(30, 30, 30, 0.7);
    border-radius: 10px;
    box-shadow: 0 10px 30px rgba(0, 255, 0, 0.1);
    backdrop-filter: blur(5px);
    border: 1px solid rgba(0, 255, 0, 0.2);
  }

  .terminal_toolbar {
    display: flex;
    height: 35px;
    align-items: center;
    padding: 0 15px;
    background: rgba(45, 45, 45, 0.8);
    justify-content: space-between;
    border-top-left-radius: 10px;
    border-top-right-radius: 10px;
    border-bottom: 1px solid rgba(0, 255, 0, 0.2);
  }

  .butt {
    display: flex;
    align-items: center;
  }

  .btn {
    height: 13px;
    width: 13px;
    border-radius: 50%;
    margin-right: 8px;
    border: none;
    cursor: pointer;
    transition: transform 0.2s ease;
  }

  .btn:hover {
    transform: scale(1.1);
  }

  .btn-color:nth-child(1) {
    background: #ff5f56;
  }
  .btn-color:nth-child(2) {
    background: #ffbd2e;
  }
  .btn-color:nth-child(3) {
    background: #27c93f;
  }

  .add_tab {
    border: none;
    color: #ffffff;
    background: #3a3a3a;
    padding: 5px 10px;
    border-radius: 5px;
    font-size: 12px;
    cursor: pointer;
    transition: background 0.2s ease;
  }

  .add_tab:hover {
    background: #4a4a4a;
  }

  .user {
    color: #ffffff;
    font-size: 14px;
    font-weight: bold;
  }

  .terminal_body {
    background: transparent;
    height: calc(100% - 35px);
    padding: 15px;
    font-family: "Consolas", monospace;
    font-size: 14px;
    line-height: 1.5;
    overflow-y: auto;
  }

  .terminal_promt {
    display: flex;
    align-items: center;
    margin-bottom: 10px;
  }

  .terminal_promt span {
    margin-right: 5px;
  }

  .terminal_user {
    color: #00ff9c;
  }
  .terminal_location {
    color: #0066ff;
  }
  .terminal_bling {
    color: #ff00ff;
  }
  .input {
    color: #ffff;
  }

  .terminal_cursor {
    display: inline-block;
    width: 8px;
    height: 18px;
    background: #ffffff;
    animation: blink 1s step-end infinite;
  }

  @keyframes blink {
    0%,
    100% {
      opacity: 1;
    }
    50% {
      opacity: 0;
    }
  }
`;

const ProjectsContainer = styled.div`
  display: flex;
  flex-wrap: wrap; /* Allow cards to wrap to the next line */
  gap: 10px; /* Space between cards */
`;

const ProjectCard = styled.div`
  border: 2px dashed #00ff9c; /* ASCII art style */
  padding: 30px;
  margin: 30px;
  background: rgba(30, 30, 30, 0.8);
  border-radius: 50px;
  flex: 1 1 calc(30% - 20px); /* Responsive card width */
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
  transition: transform 0.2s;

  &:hover {
    transform: scale(1.05); /* Slightly enlarge on hover */
  }

  .project_link {
    border: 5px dashed #00ff9c; /* ASCII art style */
    padding: 6px;
    color: #00ff9c;
    text-decoration: none;
    font-weight: bold;
    font-size: 15px;

    &:hover {
      text-decoration: underline;
    }
  }
  .header {
    color: #00ff9c;
    text-decoration: none;
    font-weight: bold;
  }
  .paragraph {
    color: #00ff9c;
    text-decoration: none;
    font-weight: bold;
    font-size: 14px;
  }
`;


const Terminal = ({ pinnedItems }) => {
  const [prompts, setPrompts] = useState([]);
  const [commandIndex, setCommandIndex] = useState(0);
  const [showProjects, setShowProjects] = useState(false);
  const [hasStarted, setHasStarted] = useState(false);
  const terminalRef = useRef(null);

  const commands = [
    { command: "cd projects", location: "/Github", delay: 0 },
    { command: "ls", location: "/Github/Projects", delay: 1000 },
  ];

  const startTypingEffect = (command, promptIndex, callback, delay = 0) => {
    let charIndex = 0;

    const typingTimer = setInterval(() => {
      if (charIndex < command.length) {
        setPrompts((prevPrompts) => {
          const updatedPrompts = [...prevPrompts];
          updatedPrompts[promptIndex] = {
            ...updatedPrompts[promptIndex],
            text: command.slice(0, charIndex + 1),
            location: commands[promptIndex].location
          };
          return updatedPrompts;
        });
        charIndex++;
      } else {
        clearInterval(typingTimer);
        setTimeout(() => {
          callback();
          if (promptIndex === commands.length - 1) {
            setShowProjects(true);
          }
        }, delay);
      }
    }, 150);
  };

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (entry.isIntersecting && !hasStarted) {
          setHasStarted(true);
          setCommandIndex(0); // Start the command sequence
        }
      },
      {
        threshold: 0.3, // Trigger when 30% of the terminal is visible
        rootMargin: '0px'
      }
    );

    if (terminalRef.current) {
      observer.observe(terminalRef.current);
    }

    return () => {
      if (terminalRef.current) {
        observer.unobserve(terminalRef.current);
      }
    };
  }, [hasStarted]);

  useEffect(() => {
    if (hasStarted && commandIndex < commands.length) {
      const { command, location, delay } = commands[commandIndex];
      setPrompts(prev => [...prev.slice(0, commandIndex), { location, text: "" }]);
      startTypingEffect(command, commandIndex, () => {
        setCommandIndex(prev => prev + 1);
      }, delay);
    }
  }, [commandIndex, hasStarted]);

  const getFirstParagraph = (readmeText) => {
    if (!readmeText) return null;
    const paragraphs = readmeText.split(/\n\n/);
    return paragraphs[1]?.trim();
  };

  const enhancedPinnedItems = pinnedItems.map((item) => ({
    ...item,
    readmeFirstParagraph: getFirstParagraph(item.object.text),
  }));

  return (
    <StyledWrapper ref={terminalRef} style={{ position: "relative", zIndex: 2, margin: "2rem auto" }}>
      <div className="container">
        <div className="terminal_toolbar">
          <div className="butt">
            <button className="btn btn-color" />
            <button className="btn" />
            <button className="btn" />
          </div>
          <p className="user">Github Projects</p>
          <button className="add_tab">+</button>
        </div>
        <div className="terminal_body">
        {prompts.map((prompt, index) => (
           <div className="terminal_promt" key={index}>
           <span className="terminal_user">JackShkifati@admin{prompt.location}:</span> 
           <span className="terminal_bling">$</span>
           <span className="input">{prompt.text}</span>
         </div>

          ))}

          {showProjects && (
            <ProjectsContainer>
              {enhancedPinnedItems.length > 0 ? (
                enhancedPinnedItems.map((item) => (
                  <ProjectCard key={item.id}>
                    <h2 className="header">{item.name}</h2>
                    <div className="paragraph">
                      {item.readmeFirstParagraph && (
                        <p className="readme_paragraph">{item.readmeFirstParagraph}</p>
                      )}
                    </div>
                    <br />
                    <div style={{ textAlign: "center" }}>
                      <a href={item.url} className="project_link" target="_blank">
                        Github Link
                      </a>
                    </div>
                  </ProjectCard>
                ))
              ) : (
                <div className="no_projects_found" style={{ color: "#ffff" }}>
                  No pinned repositories found
                </div>
              )}
            </ProjectsContainer>
          )}
        </div>
      </div>
    </StyledWrapper>
  );
};
export default Terminal;
